import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Navigate, useParams } from 'react-router-dom';

import { PATH } from 'src/features/navigation/utils/constants';
import { useCreateSystemSetting } from 'src/features/systems/api/systemSettings/useCreateSystemSetting';
import {
    LIST_SYSTEM_SETTING_RESOURCE,
    useListSystemSetting,
} from 'src/features/systems/api/systemSettings/useListSystemSetting';
import { useUpdateSystemSetting } from 'src/features/systems/api/systemSettings/useUpdateSystemSetting';
import DisplaySettingCodeInputs from 'src/features/systems/components/displaySettings/DisplaySettingCodeInputs';
import {
    DISPLAY_SETTING_ACTIONS,
    DisplaySettingAction,
    buildDisplaySettingCode,
    isGatewayDisplayModel,
    supportsDisplayColorSettings,
} from 'src/features/systems/utils/displaySettingConstants';
import {
    DisplaySettingCreateFormSchemaValues,
    DisplaySettingFormSchemaValues,
    DisplaySettingValueSchemaValues,
    displaySettingFormSchema,
} from 'src/features/systems/utils/displaySettingFormSchema';
import { displaySettingsMessages } from 'src/features/systems/utils/displaySettingsMessages';
import { DisplaySetting } from 'src/features/systems/utils/displaySettingTypes';
import { GATEWAY_DISPLAY_MODEL, SYSTEM_SETTINGS_CATEGORY } from 'src/features/systems/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Form } from 'src/registry/ui/Form';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import BackButton from 'src/features/navigation/components/BackButton';

const ACTION_SECTION_TITLE_MESSAGES: Record<
    DisplaySettingAction,
    typeof displaySettingsMessages.successfulEntranceMessage
> = {
    successful_entrance_message: displaySettingsMessages.successfulEntranceMessage,
    rejected_entrance_message: displaySettingsMessages.rejectedEntranceMessage,
    successful_exit_message: displaySettingsMessages.successfulExitMessage,
    rejected_exit_message: displaySettingsMessages.rejectedExitMessage,
    date_message: displaySettingsMessages.dateMessage,
    spaces_message: displaySettingsMessages.spacesMessage,
    lpr_message: displaySettingsMessages.lprMessage,
    ticket_message: displaySettingsMessages.ticketMessage,
    card_message: displaySettingsMessages.cardMessage,
    exit_payment_due_amount_message: displaySettingsMessages.exitPaymentDueAmountMessage,
    exit_payment_insert_card_message: displaySettingsMessages.exitPaymentInsertCardMessage,
    exit_payment_card_accepted_message: displaySettingsMessages.exitPaymentCardAcceptedMessage,
    exit_payment_card_rejected_message: displaySettingsMessages.exitPaymentCardRejectedMessage,
    exit_payment_pin_requested_message: displaySettingsMessages.exitPaymentPinRequestedMessage,
    exit_payment_processing_message: displaySettingsMessages.exitPaymentProcessingMessage,
    exit_payment_confirmed_message: displaySettingsMessages.exitPaymentConfirmedMessage,
    exit_payment_failed_message: displaySettingsMessages.exitPaymentFailedMessage,
};

const DISPLAY_MODEL_TITLE_MESSAGES: Record<GATEWAY_DISPLAY_MODEL, typeof displaySettingsMessages.spDisplay> = {
    [GATEWAY_DISPLAY_MODEL.SP]: displaySettingsMessages.spDisplay,
    [GATEWAY_DISPLAY_MODEL.HUIDU]: displaySettingsMessages.huiduDisplay,
    [GATEWAY_DISPLAY_MODEL.NED200]: displaySettingsMessages.ned200Display,
    [GATEWAY_DISPLAY_MODEL.NED200C]: displaySettingsMessages.ned200cDisplay,
};

const sanitizeValue = (
    value: DisplaySettingValueSchemaValues,
    displayModel: GATEWAY_DISPLAY_MODEL,
): DisplaySettingValueSchemaValues => {
    if (supportsDisplayColorSettings(displayModel)) return value;
    const { title_color, subtitle_color, ...rest } = value;
    void title_color;
    void subtitle_color;
    return rest;
};

const DisplaySettingsFormModel = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { displayModel: displayModelParam } = useParams();
    const createToastShown = React.useRef(false);
    const updateToastShown = React.useRef(false);

    const { setTitle, removeTitle } = useTitleStore();

    const isValidModel = isGatewayDisplayModel(displayModelParam);
    const displayModel = isValidModel ? (displayModelParam as GATEWAY_DISPLAY_MODEL) : GATEWAY_DISPLAY_MODEL.SP;

    React.useEffect(() => {
        if (!isValidModel) return;
        setTitle(formatMessage(DISPLAY_MODEL_TITLE_MESSAGES[displayModel]));
        return removeTitle;
    }, [displayModel, formatMessage, isValidModel, removeTitle, setTitle]);

    const { data: settingsData } = useListSystemSetting({ parkingId });

    const { mutate: createDisplaySetting, isPending } = useCreateSystemSetting({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_SETTING_RESOURCE] });
            if (!createToastShown.current) {
                createToastShown.current = true;
                toast({
                    title: formatMessage(displaySettingsMessages.displaySettingsCreated),
                    description: formatMessage(displaySettingsMessages.displaySettingsCreatedSuccess),
                });
            }
        },
    });

    const { mutate: updateDisplaySetting, isPending: isUpdatePending } = useUpdateSystemSetting({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_SETTING_RESOURCE] });
            if (!updateToastShown.current) {
                updateToastShown.current = true;
                toast({
                    title: formatMessage(displaySettingsMessages.displaySettingsUpdated),
                    description: formatMessage(displaySettingsMessages.displaySettingsUpdatedSuccess),
                });
            }
        },
    });

    const settingsByCode: Record<string, DisplaySetting | undefined> = React.useMemo(() => {
        const acc: Record<string, DisplaySetting | undefined> = {};
        const settings = (settingsData?.results as DisplaySetting[] | undefined) ?? [];
        for (const setting of settings) {
            acc[setting.code] = setting;
        }
        return acc;
    }, [settingsData]);

    const defaultValues = React.useMemo(() => {
        return DISPLAY_SETTING_ACTIONS.reduce<DisplaySettingFormSchemaValues>((acc, action) => {
            const code = buildDisplaySettingCode(displayModel, action);
            acc[action] = (settingsByCode[code]?.value as DisplaySettingValueSchemaValues) ?? {};
            return acc;
        }, {} as DisplaySettingFormSchemaValues);
    }, [displayModel, settingsByCode]);

    const form = useForm<DisplaySettingFormSchemaValues>({
        resolver: zodResolver(displaySettingFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues]);

    if (!isValidModel) {
        return <Navigate to={`/${PATH.ADMINISTRATION}/system-settings?tab=display-settings`} replace />;
    }

    const onSubmit: SubmitHandler<DisplaySettingFormSchemaValues> = (values) => {
        createToastShown.current = false;
        updateToastShown.current = false;
        const dirtyFields = form.formState.dirtyFields;

        DISPLAY_SETTING_ACTIONS.forEach((action) => {
            if (!dirtyFields[action]) return;

            const code = buildDisplaySettingCode(displayModel, action);
            const existingSetting = settingsByCode[code];
            const submitData: DisplaySettingCreateFormSchemaValues = {
                category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
                code,
                value: sanitizeValue(values[action], displayModel),
            };

            if (existingSetting?.id) {
                updateDisplaySetting({ id: existingSetting.id, parkingId, ...submitData });
            } else {
                createDisplaySetting({ ...submitData, parkingId });
            }
        });
    };

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/system-settings?tab=display-settings`} />
            <SectionBlock>
                <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                    <SectionBlockHead>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {formatMessage(DISPLAY_MODEL_TITLE_MESSAGES[displayModel])}
                        </SectionBlockTitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <SectionBlockBody>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                        {DISPLAY_SETTING_ACTIONS.map((action) => (
                            <DisplaySettingCodeInputs
                                key={action}
                                form={form}
                                code={action}
                                displayModel={displayModel}
                                sectionTitle={formatMessage(ACTION_SECTION_TITLE_MESSAGES[action])}
                            />
                        ))}
                        <div className="flex justify-end">
                            <Button
                                className="w-full sm:w-auto"
                                type="submit"
                                disabled={isPending || isUpdatePending || !form.formState.isDirty}
                            >
                                {(isPending || isUpdatePending) && (
                                    <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="animate-spin text-lg" />
                                )}
                                {formatMessage(commonMessages.saveChanges)}
                            </Button>
                        </div>
                    </Form>
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
};

export default DisplaySettingsFormModel;
